/**
 * Сервис для общения с backend API
 * 
 * Это "слой" между React компонентами и FastAPI.
 * Здесь определены все функции для отправки запросов на сервер.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Базовый URL backend API (прод: задайте REACT_APP_API_URL при сборке)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

function getApiErrorDetail(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }
  const detail = error.response?.data?.detail as unknown;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item: unknown) => {
        if (typeof item === 'object' && item !== null && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return null;
      })
      .filter(Boolean)
      .join('; ');
  }
  return null;
}

export function clearAuthStorage(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function persistAuthSession(
  accessToken: string,
  refreshToken: string,
  user?: { id: number; username: string; full_name: string },
): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

// Создаём axios "клиент" (инструмент для отправки запросов)
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Клиент без interceptors — чтобы /auth/refresh не зацикливался на 401
const refreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let refreshWaiters: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function resolveRefreshWaiters(token: string): void {
  refreshWaiters.forEach(({ resolve }) => resolve(token));
  refreshWaiters = [];
}

function rejectRefreshWaiters(error: unknown): void {
  refreshWaiters.forEach(({ reject }) => reject(error));
  refreshWaiters = [];
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error('Refresh-токен отсутствует');
  }

  const response = await refreshClient.post<LoginResponse>('/auth/refresh', {
    refresh_token: refreshToken,
  });
  const { access_token, refresh_token, user } = response.data;
  persistAuthSession(access_token, refresh_token, user);
  return access_token;
}

function redirectToLogin(): void {
  clearAuthStorage();
  if (!window.location.pathname.includes('/login')) {
    window.location.assign('/login');
  }
}

// ==========================================
// ПЕРЕХВАТЧИКИ (middleware)
// ==========================================

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url ?? '';

    const isAuthEndpoint =
      requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh');

    if (status !== 401 || !originalRequest || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newAccessToken = await refreshAccessToken();
      resolveRefreshWaiters(newAccessToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      rejectRefreshWaiters(refreshError);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ==========================================
// ИНТЕРФЕЙСЫ (типы данных)
// ==========================================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface User {
  id: number;
  username: string;
  full_name: string;
}

export interface Employee {
  id: number;
  username: string;
  email?: string;
  full_name: string;
  role?: string;
  is_active?: boolean;
  expenses_approver_id?: number | null;
}

export interface Department {
  id: number;
  name: string;
  code: string;
  is_executor: boolean;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  is_head: boolean;
}

export interface AccessModule {
  id: number;
  name: string;
  code: string;
  description?: string;
}

export interface Permission {
  id: number;
  module_id: number;
  code: string;
  description?: string;
}

export interface EmployeeRole {
  id: number;
  employee_id: number;
  role_id: number;
}

export interface RolePermission {
  id: number;
  role_id: number;
  permission_id: number;
}

export interface DepartmentAccess {
  id: number;
  role_id: number;
  department_id: number;
}

export interface AccessOverviewResponse {
  departments: Department[];
  roles: Role[];
  modules: AccessModule[];
  permissions: Permission[];
  employees: Employee[];
  employee_roles: EmployeeRole[];
  role_permissions: RolePermission[];
  department_access: DepartmentAccess[];
}

export type PermissionCode = 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT';
export type ModuleCode =
  | 'CONTRACTORS'
  | 'CONTRACTS'
  | 'TASKS'
  | 'OPERATIONS'
  | 'OPERATIONS_SYNC'
  | 'ACCESS_ADMIN'
  | 'CONTRACT_SUM'
  | 'CONTRACT_SUM_STAGES'
  | 'CONTRACT_COST'
  | 'CONTRACT_MARGIN'
  | 'CONTRACT_SOURCE_FIRST'
  | 'MASTERDATA'
  | 'INCOME'
  | 'OUTCOME'
  | 'INCOME_PLANNING'
  | 'OUTCOME_REQUEST'
  | 'OUTCOME_ISSUE'
  | 'OUTCOME_CASH'
  | 'OUTCOME_SERV'
  | 'OUTCOME_TAX'
  | 'OUTCOME_PARTNER'
  | 'OUTCOME_PLAN'
  | 'TASKS_CLOSING'
  | 'CONTRACTS_CLOSING';

export interface MyPermissionsResponse {
  user_id: number;
  permissions: Record<ModuleCode, Record<PermissionCode, boolean>>;
  department_codes?: string[];
  role_names?: string[];
  is_head?: boolean;
  is_admin?: boolean;
  expense_planfact_articles?: string[];
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

export interface Contractor {
  id: number;
  name: string;
  contractor_type: string;
  address?: string;
  phone?: string;
  email?: string;
  inn?: string;
  passport_data?: string;
  position_relation?: string;
  contact_full_name?: string;
  additional_contacts?: string;
}

export interface Contract {
  id: number;
  contractor_id: number;
  contract_number: string;
  contract_subject?: string;
  address?: string;
  cadastral_number?: string;
  contract_status: string;
  start_date: string;
  end_date?: string;
  total_amount: number;
  manager_id?: number;
  project_lead_id?: number;
  source_id?: number | null;
  primary_source_id?: number | null;
  hierarchy_level?: number;
  main_contract_id?: number;
  supplementary_contract_ids?: number[];
  has_original?: boolean;
  /** Есть запись public.closing_dates по семье договоров с этапом «Бухгалтерия» (id = 9). */
  accounting_closed?: boolean;
  payment_notes?: ContractNote[];
  bank_account_id?: number | null;
  planfact_account?: string | null;
  currency_type?: string | null;
  /** Сумма поступлений (budget_article = ДОХОДЫ) из planfact_operations по deal_name = CTR{contracts.id} для семьи договоров. */
  payment_paid?: number;
  /** Поступления (budget_article = ДОХОДЫ) по project_name = task_id для всех задач семьи договоров. */
  family_task_receipts?: number;
  /** Фактические расходы (budget_article = РАСХОДЫ, без маржи) по project_name = task_id для всех задач семьи. */
  family_actual_expenses?: number;
}

export interface ContractNote {
  id: number;
  contract_id: number;
  note: string;
  created_at: string;
  created_by_id?: number | null;
  created_by_name?: string | null;
  payment_status_id?: number | null;
  payment_status_text?: string | null;
}

export interface ClosingStageOption {
  id: number;
  name: string;
  closing_level: number;
}

export interface ContractPaymentCalendarRow {
  id: number;
  contract_id: number;
  payment_number: number;
  event?: string | null;
  closing_stage_id?: number | null;
  closing_stage_name?: string | null;
  contract_stage_id?: number | null;
  contract_stage_name?: string | null;
  days_after?: number | null;
  payment_date?: string | null;
  amount?: number | null;
  bank_account_id?: number | null;
  responsible_id?: number | null;
  responsible_name?: string | null;
  note?: string | null;
}

export interface ContractPaymentCalendarWrite {
  amount: number;
  payment_date?: string | null;
  days_after?: number | null;
  closing_stage_id?: number | null;
  contract_stage_id?: number | null;
  event?: string | null;
  responsible_id?: number | null;
}

export interface PaymentStatus {
  id: number;
  text: string;
}

export interface BankAccount {
  id: number;
  planfact_account: string;
  currency_type: string;
}

export interface TaskClosingStatus {
  stage_id: number;
  stage_name: string;
  closing_level: number;
  closing_date?: string | null;
}

export interface WorkType {
  id: number;
  name: string;
  short_name: string;
}

export interface Source {
  id: number;
  name: string;
  level: number;
  description?: string | null;
}

export interface Article {
  id: number;
  budget_article: string;
  cost_center?: string | null;
  category?: string | null;
  section?: string | null;
  margin_article?: string | null;
  planfact_article?: string | null;
  work_type_ids: number[];
  work_type_names: string[];
  department_ids: number[];
  department_names: string[];
}

export interface ArticleReferences {
  work_types: WorkType[];
  departments: Department[];
}

export type ArticlePayload = Omit<Article, 'id' | 'work_type_names' | 'department_names'>;

export type TaxRateExpenseCurrency = 'бн' | 'ип' | 'нал';

export interface TaxRate {
  id: number;
  expense_currency: TaxRateExpenseCurrency;
  start_date: string;
  end_date?: string | null;
  tax_rate: number;
}

export type TaxRatePayload = Omit<TaxRate, 'id'>;

export interface CashRate {
  id: number;
  contract_currency: TaxRateExpenseCurrency;
  expense_currency: TaxRateExpenseCurrency;
  start_date: string;
  end_date?: string | null;
  cash_rate: number;
}

export type CashRatePayload = Omit<CashRate, 'id'>;

export interface ServFeeRate {
  id: number;
  work_type_id: number;
  work_type_name: string;
  start_date: string;
  end_date?: string | null;
  serv_fee_rate: number;
}

export type ServFeeRatePayload = Omit<ServFeeRate, 'id'>;

export interface Task {
  id: number;
  contracts_task_id: number;
  contract_id: number;
  task_id: string;
  contract_number?: string;
  short_name: string;
  work_type_id: number;
  start_date: string;
  end_date?: string;
  amount: number;
  planned_cost?: number;
  service_fee?: number;
  taxes?: number;
  partner_commission?: number;
  planned_margin?: number;
  adjustment?: number;
  subsidy?: number;
  category_for_software?: string;
  manager_id?: number | null;
  project_lead_id?: number | null;
  department_ids?: number[];
  assignee_employee_ids?: number[];
  contract_stage_ids?: number[];
  task_status?: string;
  /** Дата закрытия расходов (task_closing_stages.id = 3). */
  expense_closing_date?: string | null;
  closing_statuses?: TaskClosingStatus[];
  /** Сумма поступлений (budget_article = ДОХОДЫ) из planfact_operations по project_name = task_id. */
  payment_paid?: number;
  /** Фактические расходы (budget_article = РАСХОДЫ, без статей маржи) из planfact_operations. */
  actual_expenses?: number;
}

export type ContractorPayload = Omit<Contractor, 'id'>;
export type ContractorUpdatePayload = Partial<ContractorPayload>;

export type ContractPayload = Omit<Contract, 'id'> & {
  stages: ContractStageWrite[];
};
export type ContractUpdatePayload = Partial<ContractPayload>;

export type TaskPayload = Omit<
  Task,
  'id' | 'contract_number' | 'category_for_software'
>;
export type TaskUpdatePayload = Partial<TaskPayload>;

// ==========================================
// ФУНКЦИИ АВТОРИЗАЦИИ
// ==========================================

/**
 * Вход в систему (авторизация)
 * Отправляет логин и пароль на backend
 * Получает пару access/refresh JWT токенов
 */
export const loginUser = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка авторизации: неверный логин или пароль');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ КОНТРАГЕНТОВ
// ==========================================

export const getContractors = async (): Promise<Contractor[]> => {
  try {
    const response = await api.get<Contractor[]>('/contractors/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении контрагентов');
  }
};

export const getEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await api.get<Employee[]>('/employees/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении сотрудников');
  }
};

export interface ContractFormAssigneesResponse {
  managers: Employee[];
  project_leads: Employee[];
}

export const getContractFormAssignees = async (): Promise<ContractFormAssigneesResponse> => {
  try {
    const response = await api.get<ContractFormAssigneesResponse>('/contracts/form-assignees');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении списков менеджеров и РП');
  }
};

export const getContractor = async (id: number): Promise<Contractor> => {
  try {
    const response = await api.get<Contractor>(`/contractors/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении контрагента');
  }
};

export const createContractor = async (data: ContractorPayload): Promise<Contractor> => {
  try {
    const response = await api.post<Contractor>('/contractors/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании контрагента');
  }
};

export const updateContractor = async (id: number, data: ContractorUpdatePayload): Promise<Contractor> => {
  try {
    const response = await api.put<Contractor>(`/contractors/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении контрагента');
  }
};

export const deleteContractor = async (id: number): Promise<void> => {
  try {
    await api.delete(`/contractors/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при удалении контрагента');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ ДОГОВОРОВ
// ==========================================

export const getContracts = async (): Promise<Contract[]> => {
  try {
    const response = await api.get<Contract[]>('/contracts/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении договоров');
  }
};

export const getContract = async (id: number): Promise<Contract> => {
  try {
    const response = await api.get<Contract>(`/contracts/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении договора');
  }
};

export const createContract = async (
  data: ContractPayload
): Promise<Contract> => {
  try {
    const response = await api.post<Contract>('/contracts/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании договора');
  }
};

export const updateContract = async (id: number, data: ContractUpdatePayload): Promise<Contract> => {
  try {
    const response = await api.put<Contract>(`/contracts/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении договора');
  }
};

export const deleteContract = async (id: number): Promise<void> => {
  try {
    await api.delete(`/contracts/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении договора');
  }
};

export const createContractPaymentNote = async (
  contractId: number,
  note: string,
  paymentStatusId?: number | null,
): Promise<ContractNote> => {
  try {
    const response = await api.post<ContractNote>(`/contracts/${contractId}/payment-notes`, {
      note,
      payment_status_id: paymentStatusId ?? null,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при добавлении примечания по оплатам');
  }
};

export const getClosingStages = async (): Promise<ClosingStageOption[]> => {
  try {
    const response = await api.get<ClosingStageOption[]>('/closing-stages/');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении этапов закрытия');
  }
};

export const getContractPaymentCalendar = async (
  contractId: number,
): Promise<ContractPaymentCalendarRow[]> => {
  try {
    const response = await api.get<ContractPaymentCalendarRow[]>(
      `/contracts/${contractId}/payment-calendar`,
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении графика поступлений');
  }
};

export const replaceContractPaymentCalendar = async (
  contractId: number,
  items: ContractPaymentCalendarWrite[],
): Promise<ContractPaymentCalendarRow[]> => {
  try {
    const response = await api.put<ContractPaymentCalendarRow[]>(
      `/contracts/${contractId}/payment-calendar`,
      { items },
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при сохранении графика поступлений');
  }
};

export interface ContractStage {
  id: number;
  contract_id: number;
  stage_number: number;
  name: string;
  result?: string | null;
  planned_date_start?: string | null;
  planned_date_end?: string | null;
  extends_stage_id?: number | null;
  accounting_closed?: boolean;
  accounting_closing_date?: string | null;
}

export interface ContractStageWrite {
  id?: number | null;
  client_key?: string | null;
  contract_id?: number | null;
  name: string;
  result?: string | null;
  planned_date_start: string;
  planned_date_end: string;
  extends_stage_id?: number | null;
  extends_client_key?: string | null;
}

export const getContractStages = async (contractId: number): Promise<ContractStage[]> => {
  try {
    const response = await api.get<ContractStage[]>(`/contracts/${contractId}/stages`);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении графика работ');
  }
};

export const replaceContractStages = async (
  contractId: number,
  items: ContractStageWrite[],
  options?: { family?: boolean },
): Promise<ContractStage[]> => {
  try {
    const response = await api.put<ContractStage[]>(`/contracts/${contractId}/stages`, {
      items,
      family: options?.family ?? false,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при сохранении графика работ');
  }
};

export const closeContractStage = async (
  contractId: number,
  stageId: number,
): Promise<ClosingDateRow> => {
  try {
    const response = await api.post<ClosingDateRow>(
      `/contracts/${contractId}/stages/${stageId}/close`,
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при закрытии этапа работ');
  }
};

export const getPaymentStatuses = async (): Promise<PaymentStatus[]> => {
  try {
    const response = await api.get<PaymentStatus[]>('/payment-statuses/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении статусов оплат');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ СТАТЕЙ (МАСТЕРДАННЫЕ)
// ==========================================

export const getArticleReferences = async (): Promise<ArticleReferences> => {
  try {
    const response = await api.get<ArticleReferences>('/articles/references');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении справочников для статей');
  }
};

export const getArticles = async (): Promise<Article[]> => {
  try {
    const response = await api.get<Article[]>('/articles/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении статей');
  }
};

export const createArticle = async (data: ArticlePayload): Promise<Article> => {
  try {
    const response = await api.post<Article>('/articles/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании статьи');
  }
};

export const updateArticle = async (id: number, data: Partial<ArticlePayload>): Promise<Article> => {
  try {
    const response = await api.put<Article>(`/articles/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении статьи');
  }
};

export const deleteArticle = async (id: number): Promise<void> => {
  try {
    await api.delete(`/articles/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении статьи');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ НАЛОГОВ (МАСТЕРДАННЫЕ)
// ==========================================

export const getTaxRates = async (): Promise<TaxRate[]> => {
  try {
    const response = await api.get<TaxRate[]>('/tax-rates/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении ставок налога');
  }
};

export const createTaxRate = async (data: TaxRatePayload): Promise<TaxRate> => {
  try {
    const response = await api.post<TaxRate>('/tax-rates/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании ставки налога');
  }
};

export const updateTaxRate = async (id: number, data: Partial<TaxRatePayload>): Promise<TaxRate> => {
  try {
    const response = await api.put<TaxRate>(`/tax-rates/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении ставки налога');
  }
};

export const deleteTaxRate = async (id: number): Promise<void> => {
  try {
    await api.delete(`/tax-rates/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении ставки налога');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ КЭША (МАСТЕРДАННЫЕ)
// ==========================================

export const getCashRates = async (): Promise<CashRate[]> => {
  try {
    const response = await api.get<CashRate[]>('/cash-rates/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении ставок кэша');
  }
};

export const createCashRate = async (data: CashRatePayload): Promise<CashRate> => {
  try {
    const response = await api.post<CashRate>('/cash-rates/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании ставки кэша');
  }
};

export const updateCashRate = async (id: number, data: Partial<CashRatePayload>): Promise<CashRate> => {
  try {
    const response = await api.put<CashRate>(`/cash-rates/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении ставки кэша');
  }
};

export const deleteCashRate = async (id: number): Promise<void> => {
  try {
    await api.delete(`/cash-rates/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении ставки кэша');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ СЕРВИСНОГО СБОРА (МАСТЕРДАННЫЕ)
// ==========================================

export const getServFeeRates = async (): Promise<ServFeeRate[]> => {
  try {
    const response = await api.get<ServFeeRate[]>('/serv-fee-rates/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении ставок сервисного сбора');
  }
};

export const createServFeeRate = async (data: ServFeeRatePayload): Promise<ServFeeRate> => {
  try {
    const response = await api.post<ServFeeRate>('/serv-fee-rates/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании ставки сервисного сбора');
  }
};

export const updateServFeeRate = async (id: number, data: Partial<ServFeeRatePayload>): Promise<ServFeeRate> => {
  try {
    const response = await api.put<ServFeeRate>(`/serv-fee-rates/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении ставки сервисного сбора');
  }
};

export const deleteServFeeRate = async (id: number): Promise<void> => {
  try {
    await api.delete(`/serv-fee-rates/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении ставки сервисного сбора');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ ЗАДАЧ
// ==========================================

export const getWorkTypes = async (): Promise<WorkType[]> => {
  try {
    const response = await api.get<WorkType[]>('/work-types/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении видов работ');
  }
};

export const getSources = async (level: 1 | 2): Promise<Source[]> => {
  try {
    const response = await api.get<Source[]>('/sources/', { params: { level } });
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении справочника источников');
  }
};

export const createSource = async (data: {
  name: string;
  level: 1 | 2;
  description?: string;
}): Promise<Source> => {
  try {
    const response = await api.post<Source>('/sources/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании источника');
  }
};

export const getBankAccounts = async (): Promise<BankAccount[]> => {
  try {
    const response = await api.get<BankAccount[]>('/bank-accounts/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении счетов оплаты');
  }
};

export const getTasks = async (): Promise<Task[]> => {
  try {
    const response = await api.get<Task[]>('/tasks/');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении задач');
  }
};

export type SmoUlClosingStatus = 'na' | 'open' | 'partial' | 'waiting' | 'closed';

export interface SmoUlClosingStageColumn {
  stage_id: number;
  stage_name: string;
}

export interface SmoUlClosingStatusCell {
  stage_id: number;
  status: SmoUlClosingStatus;
  closing_date?: string | null;
}

export interface ClosingTaskWorkStage {
  contract_stage_id: number;
  stage_number: number;
  name: string;
  status: SmoUlClosingStatus;
  closing_date?: string | null;
}

export interface ClosingTask {
  id: number;
  contracts_task_id: number;
  task_id: string;
  short_name: string;
  manager_id?: number | null;
  manager_name: string;
  project_lead_id?: number | null;
  project_lead_name: string;
  start_date: string;
  end_date?: string | null;
  expense_closing_date?: string | null;
  task_status?: string;
  closing_statuses: SmoUlClosingStatusCell[];
  work_stages?: ClosingTaskWorkStage[];
}

export interface ClosingTaskAction {
  kind: 'department' | 'expenses';
  stage_id: number;
  department_id?: number | null;
  label: string;
}

export interface ClosingTasksResponse {
  executor_stages: SmoUlClosingStageColumn[];
  rp_stage?: SmoUlClosingStageColumn | null;
  company_stages: SmoUlClosingStageColumn[];
  actions: ClosingTaskAction[];
  tasks: ClosingTask[];
}

export const getClosingTasks = async (): Promise<ClosingTasksResponse> => {
  try {
    const response = await api.get<ClosingTasksResponse>('/closing/tasks');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении задач к закрытию');
  }
};

export interface ClosingContractCheck {
  status: SmoUlClosingStatus;
  closing_date?: string | null;
}

export interface ClosingContract {
  id: number;
  contract_number: string;
  contractor_id: number;
  contractor_name: string;
  address?: string | null;
  cadastral_number?: string | null;
  manager_id?: number | null;
  manager_name: string;
  project_lead_id?: number | null;
  project_lead_name: string;
  start_date?: string | null;
  end_date?: string | null;
  expense_closing_date?: string | null;
  contract_status?: string;
  total_amount?: number;
  hierarchy_level?: number;
  main_contract_id: number;
  main_contract_number: string;
  expenses_closed: ClosingContractCheck; // все этапы закрыты бухгалтерией
  fully_paid: ClosingContractCheck; // полная оплата по договору
}

export interface ClosingContractsResponse {
  contracts: ClosingContract[];
}

export const getClosingContracts = async (): Promise<ClosingContractsResponse> => {
  try {
    const response = await api.get<ClosingContractsResponse>('/closing/contracts');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении договоров к закрытию');
  }
};

export const closeClosingContracts = async (payload: {
  contract_ids: number[];
}): Promise<number> => {
  try {
    const response = await api.post<{ created_count: number }>('/closing/contracts', payload);
    return response.data.created_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при закрытии договоров');
  }
};

export interface ClosingWorkStage {
  id: number;
  stage_number: number;
  stage_name: string;
  contract_id: number;
  contract_number: string;
  contractor_id: number;
  contractor_name: string;
  manager_id?: number | null;
  manager_name: string;
  project_lead_id?: number | null;
  project_lead_name: string;
  start_date?: string | null;
  end_date?: string | null;
  result?: string | null;
  contract_status?: string;
  hierarchy_level?: number;
  main_contract_id: number;
  main_contract_number: string;
  extends_stage_id?: number | null;
  accounting_closed: boolean;
  accounting_closing_date?: string | null;
}

export interface ClosingWorkStagesResponse {
  stages: ClosingWorkStage[];
}

export const getClosingWorkStages = async (): Promise<ClosingWorkStagesResponse> => {
  try {
    const response = await api.get<ClosingWorkStagesResponse>('/closing/stages');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении этапов к закрытию');
  }
};

export const closeClosingWorkStages = async (payload: {
  stage_ids: number[];
  closing_date: string;
}): Promise<number> => {
  try {
    const response = await api.post<{ created_count: number }>('/closing/stages', payload);
    return response.data.created_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при закрытии этапов');
  }
};

export const closeTasks = async (payload: {
  task_ids: number[];
  kind: ClosingTaskAction['kind'];
  department_id?: number | null;
}): Promise<number> => {
  try {
    const response = await api.post<{ created_count: number }>('/closing/tasks', payload);
    return response.data.created_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при закрытии задач');
  }
};

export interface ClosingDateRow {
  id: number;
  entity: string;
  task_id?: number | null;
  task_code: string;
  task_short_name: string;
  contract_id?: number | null;
  contract_number?: string;
  contract_stage_id?: number | null;
  contract_stage_name?: string;
  closing_stage_id: number;
  closing_stage_name: string;
  closing_date: string;
  note?: string | null;
  closed_by?: number | null;
  closed_by_name: string;
  closed_by_user?: number | null;
  closed_by_user_name: string;
}

export interface ClosingDatePayload {
  task_id?: number | null;
  contract_id?: number | null;
  contract_stage_id?: number | null;
  closing_stage_id: number;
  closing_date: string;
  note?: string | null;
  closed_by?: number | null;
  closed_by_user?: number | null;
}

export interface ClosingDateTaskOption {
  id: number;
  task_id: string;
  short_name: string;
  contract_id?: number | null;
  contract_number?: string | null;
}

export interface ClosingDateContractOption {
  id: number;
  contract_number: string;
}

export interface ClosingDateContractStageOption {
  id: number;
  contract_id: number;
  contract_number: string;
  stage_number: number;
  name: string;
}

export interface ClosingDateHeadEmployee {
  id: number;
  full_name: string;
}

export interface ClosingDateDepartmentHeads {
  department_id: number;
  employees: ClosingDateHeadEmployee[];
}

export interface ClosingDateReferences {
  stages: SmoUlClosingStageColumn[];
  departments: Department[];
  department_heads: ClosingDateDepartmentHeads[];
  tasks: ClosingDateTaskOption[];
  contracts?: ClosingDateContractOption[];
  contract_stages?: ClosingDateContractStageOption[];
}

export const getClosingDates = async (): Promise<ClosingDateRow[]> => {
  try {
    const response = await api.get<ClosingDateRow[]>('/closing/dates');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении журнала закрытия');
  }
};

export const getClosingDateReferences = async (): Promise<ClosingDateReferences> => {
  try {
    const response = await api.get<ClosingDateReferences>('/closing/dates/references');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении справочников закрытия');
  }
};

export const createClosingDate = async (data: ClosingDatePayload): Promise<ClosingDateRow> => {
  try {
    const response = await api.post<ClosingDateRow>('/closing/dates', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании записи закрытия');
  }
};

export const updateClosingDate = async (
  id: number,
  data: ClosingDatePayload,
): Promise<ClosingDateRow> => {
  try {
    const response = await api.put<ClosingDateRow>(`/closing/dates/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при обновлении записи закрытия');
  }
};

export const deleteClosingDate = async (id: number): Promise<void> => {
  try {
    await api.delete(`/closing/dates/${id}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при удалении записи закрытия');
  }
};

export interface InProgressTask {
  id: number;
  contracts_task_id: number;
  task_id: string;
  short_name: string;
  manager_id?: number | null;
  manager_name: string;
  project_lead_id?: number | null;
  project_lead_name: string;
  start_date: string;
  end_date?: string | null;
  expense_closing_date?: string | null;
  plan_expenses?: number | null;
  fact_expenses: number;
  delta?: number | null;
  task_status?: string;
}

export interface InProgressTasksResponse {
  tasks: InProgressTask[];
}

export const getInProgressTasks = async (): Promise<InProgressTasksResponse> => {
  try {
    const response = await api.get<InProgressTasksResponse>('/in-progress/tasks');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при получении задач в работе');
  }
};

export const getTask = async (id: number): Promise<Task> => {
  try {
    const response = await api.get<Task>(`/tasks/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении задачи');
  }
};

export const createTask = async (
  data: TaskPayload
): Promise<Task> => {
  try {
    const response = await api.post<Task>('/tasks/', data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при создании задачи');
  }
};

export const updateTask = async (id: number, data: TaskUpdatePayload): Promise<Task> => {
  try {
    const response = await api.put<Task>(`/tasks/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при обновлении задачи');
  }
};

export const deleteTask = async (contractsTaskId: number): Promise<void> => {
  try {
    await api.delete(`/tasks/links/${contractsTaskId}`);
  } catch (error) {
    throw new Error(getApiErrorDetail(error) ?? 'Ошибка при удалении задачи');
  }
};

/** Отделы с признаком «исполнитель» (для назначения на задачу). */
export const getExecutorDepartments = async (): Promise<Department[]> => {
  try {
    const response = await api.get<Department[]>('/departments/executors');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении отделов-исполнителей');
  }
};

export interface ExecutorDepartmentEmployeesItem {
  department_id: number;
  employee_ids: number[];
}

/** Сотрудники с доступом к отделу-исполнителю через роль (department_access). */
export const getExecutorDepartmentEmployees = async (): Promise<ExecutorDepartmentEmployeesItem[]> => {
  try {
    const response = await api.get<ExecutorDepartmentEmployeesItem[]>(
      '/departments/executors/employees-by-access',
    );
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении сотрудников отделов-исполнителей');
  }
};

// ==========================================
// ФУНКЦИИ ДЛЯ АДМИНИСТРИРОВАНИЯ ДОСТУПОВ
// ==========================================

export const getAccessOverview = async (): Promise<AccessOverviewResponse> => {
  try {
    const response = await api.get<AccessOverviewResponse>('/admin/access/overview');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при загрузке данных по доступам');
  }
};

export const getAccessUsers = async (): Promise<Employee[]> => {
  try {
    const response = await api.get<Employee[]>('/admin/access/users');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении пользователей');
  }
};

export const createAccessUser = async (
  payload: Omit<Employee, 'id'> & { password: string },
): Promise<Employee> => {
  try {
    const response = await api.post<Employee>('/admin/access/users', payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при создании пользователя');
  }
};

export const updateAccessUser = async (
  userId: number,
  payload: Omit<Employee, 'id'> & { password?: string },
): Promise<Employee> => {
  try {
    const response = await api.put<Employee>(`/admin/access/users/${userId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении пользователя');
  }
};

export const deleteAccessUser = async (userId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/users/${userId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении пользователя');
  }
};

export const createAccessRole = async (payload: Omit<Role, 'id'>): Promise<Role> => {
  try {
    const response = await api.post<Role>('/admin/access/roles', payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при создании роли');
  }
};

export const updateAccessRole = async (roleId: number, payload: Omit<Role, 'id'>): Promise<Role> => {
  try {
    const response = await api.put<Role>(`/admin/access/roles/${roleId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении роли');
  }
};

export const deleteAccessRole = async (roleId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/roles/${roleId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении роли');
  }
};

export const createAccessModule = async (payload: Omit<AccessModule, 'id'>): Promise<AccessModule> => {
  try {
    const response = await api.post<AccessModule>('/admin/access/modules', payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при создании модуля');
  }
};

export const updateAccessModule = async (moduleId: number, payload: Omit<AccessModule, 'id'>): Promise<AccessModule> => {
  try {
    const response = await api.put<AccessModule>(`/admin/access/modules/${moduleId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении модуля');
  }
};

export const deleteAccessModule = async (moduleId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/modules/${moduleId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении модуля');
  }
};

export const createAccessPermission = async (payload: Omit<Permission, 'id'>): Promise<Permission> => {
  try {
    const response = await api.post<Permission>('/admin/access/permissions', payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при создании разрешения');
  }
};

export const updateAccessPermission = async (permissionId: number, payload: Omit<Permission, 'id'>): Promise<Permission> => {
  try {
    const response = await api.put<Permission>(`/admin/access/permissions/${permissionId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении разрешения');
  }
};

export const deleteAccessPermission = async (permissionId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/permissions/${permissionId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении разрешения');
  }
};

export const createAccessDepartment = async (payload: Omit<Department, 'id'>): Promise<Department> => {
  try {
    const response = await api.post<Department>('/admin/access/departments', payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при создании отдела');
  }
};

export const updateAccessDepartment = async (departmentId: number, payload: Omit<Department, 'id'>): Promise<Department> => {
  try {
    const response = await api.put<Department>(`/admin/access/departments/${departmentId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при обновлении отдела');
  }
};

export const deleteAccessDepartment = async (departmentId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/departments/${departmentId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении отдела');
  }
};

export const assignRoleToEmployee = async (employeeId: number, roleId: number): Promise<EmployeeRole> => {
  try {
    const response = await api.post<EmployeeRole>(`/admin/access/employees/${employeeId}/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при назначении роли сотруднику');
  }
};

export const revokeRoleFromEmployee = async (employeeId: number, roleId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/employees/${employeeId}/roles/${roleId}`);
  } catch (error) {
    throw new Error('Ошибка при снятии роли с сотрудника');
  }
};

export const grantPermissionToRole = async (roleId: number, permissionId: number): Promise<RolePermission> => {
  try {
    const response = await api.post<RolePermission>(`/admin/access/roles/${roleId}/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при назначении разрешения роли');
  }
};

export const revokePermissionFromRole = async (roleId: number, permissionId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/roles/${roleId}/permissions/${permissionId}`);
  } catch (error) {
    throw new Error('Ошибка при снятии разрешения у роли');
  }
};

export const upsertRoleDepartmentAccess = async (
  roleId: number,
  departmentId: number,
): Promise<DepartmentAccess> => {
  try {
    const response = await api.put<DepartmentAccess>(
      `/admin/access/roles/${roleId}/departments/${departmentId}`,
      {},
    );
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при сохранении доступа роли к отделу');
  }
};

export const deleteRoleDepartmentAccess = async (roleId: number, departmentId: number): Promise<void> => {
  try {
    await api.delete(`/admin/access/roles/${roleId}/departments/${departmentId}`);
  } catch (error) {
    throw new Error('Ошибка при удалении доступа роли к отделу');
  }
};

export interface ExpenseArticleAccessItem {
  id: number;
  planfact_article?: string | null;
  category?: string | null;
  section?: string | null;
}

export interface ArticleRoleAssignment {
  article_id: number;
  role_id: number;
}

export interface ArticleRolesAccessResponse {
  articles: ExpenseArticleAccessItem[];
  roles: Role[];
  assignments: ArticleRoleAssignment[];
}

export const getArticleRolesAccess = async (): Promise<ArticleRolesAccessResponse> => {
  try {
    const response = await api.get<ArticleRolesAccessResponse>('/admin/access/article-roles');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке доступов к статьям расходов');
  }
};

export const saveArticleRolesAccess = async (
  assignments: ArticleRoleAssignment[],
): Promise<ArticleRolesAccessResponse> => {
  try {
    const response = await api.put<ArticleRolesAccessResponse>('/admin/access/article-roles', {
      assignments,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при сохранении доступов к статьям расходов');
  }
};

export const getMyPermissions = async (): Promise<MyPermissionsResponse> => {
  try {
    const response = await api.get<MyPermissionsResponse>('/access/my-permissions');
    return response.data;
  } catch (error) {
    throw new Error('Ошибка при получении прав текущего пользователя');
  }
};

export interface PlanfactOperation {
  id: number;
  planfact_operation_id: string;
  operation_date: string;
  account_name?: string;
  account_type?: string;
  operation_type?: string;
  category_name?: string;
  budget_article?: string;
  amount: number;
  cash_amount?: number | null;
  tax_amount?: number | null;
  serv_fee_amount?: number | null;
  project_name?: string;
  deal_name?: string;
  contractor_name_pf?: string;
  comment?: string;
  modified_at?: string;
  synced_at: string;
  source?: 'planfact' | 'tax';
}

export interface PlanfactSyncResult {
  ok: boolean;
  message: string;
  synced_count: number;
  projects_matched: number;
  active_task_ids: number;
}

export const getPlanfactOperations = async (): Promise<PlanfactOperation[]> => {
  try {
    const response = await api.get<PlanfactOperation[]>('/planfact-operations/');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке операций');
  }
};

export const syncPlanfactOperations = async (): Promise<PlanfactSyncResult> => {
  try {
    const response = await api.post<PlanfactSyncResult>('/planfact-operations/sync');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка синхронизации с PlanFact');
  }
};

export const syncPlanfactOperationsChanges = async (): Promise<PlanfactSyncResult> => {
  try {
    const response = await api.post<PlanfactSyncResult>('/planfact-operations/sync-changes');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка синхронизации изменений с PlanFact');
  }
};

// ==========================================
// Распределение платежей (Income без проекта)
// ==========================================

export interface UndistributedIncome {
  id: number;
  planfact_operation_id: string;
  operation_date: string;
  account_name?: string;
  account_type?: string;
  category_name?: string;
  budget_article?: string;
  amount: number;
  deal_name?: string;
  contractor_name_pf?: string;
  comment?: string;
  modified_at?: string;
  synced_at: string;
}

export interface UndistributedIncomeSyncResult {
  ok: boolean;
  message: string;
  synced_count: number;
}

export interface PaymentDistributionMeta {
  planfact_articles: string[];
}

export interface PaymentDistributionAssignPart {
  task_id: string;
  contract_id: number;
  planfact_article: string;
  amount: number;
}

export interface PaymentDistributionAssignItem {
  planfact_operation_id: string;
  parts: PaymentDistributionAssignPart[];
}

export interface PaymentDistributionAssignResult {
  assigned_count: number;
  errors: string[];
  operations_sync_message?: string | null;
}

export interface PaymentViewOperation {
  id: number;
  planfact_operation_id: string;
  operation_date: string;
  account_name?: string;
  account_type?: string;
  category_name?: string;
  budget_article?: string;
  amount: number;
  project_name?: string;
  task_name?: string;
  deal_name?: string;
  contract_number?: string;
  contractor_name_pf?: string;
  comment?: string;
  modified_at?: string;
  synced_at: string;
}

export const getPaymentViewOperations = async (): Promise<PaymentViewOperation[]> => {
  try {
    const response = await api.get<PaymentViewOperation[]>('/payment-view/');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке поступлений');
  }
};

export const getUndistributedIncomes = async (): Promise<UndistributedIncome[]> => {
  try {
    const response = await api.get<UndistributedIncome[]>('/payment-distribution/');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке нераспределённых поступлений');
  }
};

export const getPaymentDistributionMeta = async (): Promise<PaymentDistributionMeta> => {
  try {
    const response = await api.get<PaymentDistributionMeta>('/payment-distribution/meta');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке справочника статей');
  }
};

export const syncUndistributedIncomes = async (): Promise<UndistributedIncomeSyncResult> => {
  try {
    const response = await api.post<UndistributedIncomeSyncResult>('/payment-distribution/sync');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка синхронизации поступлений без проекта');
  }
};

export const assignUndistributedIncomes = async (
  items: PaymentDistributionAssignItem[],
): Promise<PaymentDistributionAssignResult> => {
  try {
    const response = await api.post<PaymentDistributionAssignResult>('/payment-distribution/assign', {
      items,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка отправки в PlanFact');
  }
};

// ==========================================
// Заявки на выдачу ДС
// ==========================================

export interface PaymentRequestFormTaskOption {
  task_id: string;
  short_name: string;
}

export interface PaymentRequestFormRequestorOption {
  id: number;
  full_name: string;
}

export interface PaymentRequestFormMeta {
  request_date: string;
  requestor_id: number;
  requestor_name: string;
  approver_id?: number | null;
  approver_name?: string | null;
  can_select_requestor?: boolean;
  requestors?: PaymentRequestFormRequestorOption[];
  planfact_articles: string[];
  tasks: PaymentRequestFormTaskOption[];
}

export interface PaymentRequestCreateItem {
  task_id: string;
  planfact_article: string;
  amount: number;
  note?: string | null;
}

export interface PaymentRequest {
  id: number;
  request_date: string;
  task_id: string;
  planfact_article: string;
  amount: number;
  note?: string | null;
  requestor_id: number;
  approver_id?: number | null;
  approved_at?: string | null;
  issued_at?: string | null;
  rejected_at?: string | null;
  rejected_reason?: string | null;
  payment_account?: number | null;
  payment_account_name?: string | null;
}

export interface PaymentRequestListItem extends PaymentRequest {
  requestor_name: string;
  approver_name?: string | null;
  task_short_name?: string | null;
}

export interface PaymentRequestIssueItem {
  id: number;
  payment_account: number;
}

export interface PaymentRequestIssueResult {
  updated_count: number;
  synced_count: number;
  sync_errors: string[];
}

export interface PaymentRequestBatchCreateResponse {
  created_count: number;
  items: PaymentRequest[];
}

export interface PaymentRequestSpentItem {
  task_id: string;
  planfact_article: string;
  plan?: number | null;
  spent: number;
  delta?: number | null;
}

export const getPaymentRequestFormMeta = async (
  requestorId?: number,
): Promise<PaymentRequestFormMeta> => {
  try {
    const response = await api.get<PaymentRequestFormMeta>('/payment-requests/form-meta', {
      params: requestorId ? { requestor_id: requestorId } : undefined,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке формы заявки');
  }
};

export const getPaymentRequestSpent = async (
  items: Array<{ task_id: string; planfact_article: string }>,
): Promise<PaymentRequestSpentItem[]> => {
  if (items.length === 0) {
    return [];
  }
  try {
    const response = await api.post<{ items: PaymentRequestSpentItem[] }>('/payment-requests/spent', { items });
    return response.data.items ?? [];
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке сумм операций');
  }
};

export const getMyPaymentRequests = async (): Promise<PaymentRequestListItem[]> => {
  try {
    const response = await api.get<PaymentRequestListItem[]>('/payment-requests/my');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке заявок');
  }
};

export const getPendingApprovalPaymentRequests = async (): Promise<PaymentRequestListItem[]> => {
  try {
    const response = await api.get<PaymentRequestListItem[]>('/payment-requests/pending-approval');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке заявок на согласование');
  }
};

export const getPendingApprovalPaymentRequestsCount = async (): Promise<number> => {
  try {
    const response = await api.get<{ count: number }>('/payment-requests/pending-approval/count');
    return response.data.count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке счётчика заявок');
  }
};

export const approvePaymentRequests = async (ids: number[]): Promise<number> => {
  try {
    const response = await api.post<{ updated_count: number }>('/payment-requests/approve', { ids });
    return response.data.updated_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при согласовании заявок');
  }
};

export const rejectPaymentRequests = async (ids: number[], reason?: string | null): Promise<number> => {
  try {
    const response = await api.post<{ updated_count: number }>('/payment-requests/reject', {
      ids,
      reason: reason || null,
    });
    return response.data.updated_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при отклонении заявок');
  }
};

export const getPendingIssuePaymentRequests = async (): Promise<PaymentRequestListItem[]> => {
  try {
    const response = await api.get<PaymentRequestListItem[]>('/payment-requests/pending-issue');
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке заявок на выдачу');
  }
};

export const getPendingIssuePaymentRequestsCount = async (): Promise<number> => {
  try {
    const response = await api.get<{ count: number }>('/payment-requests/pending-issue/count');
    return response.data.count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при загрузке счётчика заявок на выдачу');
  }
};

export const issuePaymentRequests = async (
  items: PaymentRequestIssueItem[],
): Promise<PaymentRequestIssueResult> => {
  try {
    const response = await api.post<PaymentRequestIssueResult>('/payment-requests/issue', { items });
    return {
      updated_count: response.data.updated_count,
      synced_count: response.data.synced_count ?? 0,
      sync_errors: response.data.sync_errors ?? [],
    };
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при выдаче заявок');
  }
};

export const rejectIssuePaymentRequests = async (
  ids: number[],
  reason?: string | null,
): Promise<number> => {
  try {
    const response = await api.post<{ updated_count: number }>('/payment-requests/reject-issue', {
      ids,
      reason: reason || null,
    });
    return response.data.updated_count;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при отклонении заявок на выдаче');
  }
};

export const createPaymentRequests = async (
  items: PaymentRequestCreateItem[],
  requestorId?: number,
): Promise<PaymentRequestBatchCreateResponse> => {
  try {
    const response = await api.post<PaymentRequestBatchCreateResponse>('/payment-requests/', {
      items,
      requestor_id: requestorId,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorDetail(error) || 'Ошибка при сохранении заявок');
  }
};
